import { z } from 'zod';
import { isRecord } from '../../is-record';
// Call analysis fields and action variables share the editor's schema form: string, number, boolean, array and
// object, nested at most two levels, with names of letters, digits, underscores and hyphens.
const scalarTypeSchema = z.enum(['string', 'number', 'boolean']);
const fieldTypeSchema = z.enum(['string', 'number', 'boolean', 'array', 'object']);
const leafFieldSchema = z.object({
    type: scalarTypeSchema,
    description: z.string().optional(),
    enum: z.array(z.string()).optional().describe('The only values allowed, for a string field')
});
const nestedFieldSchema = leafFieldSchema.extend({
    type: fieldTypeSchema,
    items: leafFieldSchema.optional().describe('For an array: what each item is'),
    properties: z.record(z.string(), leafFieldSchema).optional().describe('For an object: its fields by name')
});
export const schemaFieldSchema = nestedFieldSchema.extend({
    items: nestedFieldSchema.optional().describe('For an array: what each item is'),
    properties: z.record(z.string(), nestedFieldSchema).optional().describe('For an object: its fields by name')
});
export const actionParameterSchema = schemaFieldSchema.extend({
    required: z.boolean().optional().describe('The agent must supply it to call the action')
});
export const SCHEMA_FIELD_NAME_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;
const toJsonSchema = (field) => ({
    type: field.type,
    ...(field.description && { description: field.description }),
    ...(field.enum?.length && { enum: field.enum }),
    ...(field.items && { items: toJsonSchema(field.items) }),
    ...(field.properties && {
        properties: Object.fromEntries(Object.entries(field.properties).map(([name, property]) => [name, toJsonSchema(property)]))
    })
});
const normalizeJsonSchema = (schema) => {
    if (!isRecord(schema))
        return schema;
    return {
        type: schema.type === 'integer' ? 'number' : schema.type,
        ...(typeof schema.description === 'string' && schema.description && { description: schema.description }),
        ...(Array.isArray(schema.enum) && {
            enum: schema.enum.filter((value) => typeof value === 'string')
        }),
        ...(isRecord(schema.items) && { items: normalizeJsonSchema(schema.items) }),
        ...(isRecord(schema.properties) && {
            properties: Object.fromEntries(Object.entries(schema.properties).map(([name, property]) => [name, normalizeJsonSchema(property)]))
        })
    };
};
// Fields the editor couldn't have produced, such as ones nested too deep, are left out.
const fromJsonSchemaProperties = (properties) => Object.fromEntries(Object.entries(properties).flatMap(([name, property]) => {
    const parsed = schemaFieldSchema.safeParse(normalizeJsonSchema(property));
    return parsed.success ? [[name, parsed.data]] : [];
}));
/** The call analysis schema as stored on the prompt (`analysisPlan.schema`). */
export const toAnalysisSchema = (fields) => ({
    type: 'object',
    properties: Object.fromEntries(Object.entries(fields).map(([name, field]) => [name, toJsonSchema(field)]))
});
export const fromAnalysisSchema = (schema) => schema && isRecord(schema.properties) ? fromJsonSchemaProperties(schema.properties) : {};
/** An action's variables as stored on its settings (`functionParameters`). */
export const toFunctionParameters = (parameters) => {
    const required = Object.entries(parameters).flatMap(([name, { required }]) => (required ? [name] : []));
    return {
        type: 'object',
        properties: Object.fromEntries(Object.entries(parameters).map(([name, { required: _required, ...field }]) => [
            name,
            toJsonSchema(field)
        ])),
        ...(required.length > 0 && { required })
    };
};
export const fromFunctionParameters = (parameters) => {
    if (!parameters)
        return {};
    const required = new Set(parameters.required ?? []);
    return Object.fromEntries(Object.entries(fromJsonSchemaProperties(parameters.properties)).map(([name, field]) => [
        name,
        { ...field, ...(required.has(name) && { required: true }) }
    ]));
};
/** Names in a field tree that the editor wouldn't accept, as dot paths. */
export const findInvalidFieldNames = (fields, prefix = '') => Object.entries(fields).flatMap(([name, field]) => [
    ...(SCHEMA_FIELD_NAME_PATTERN.test(name) ? [] : [`${prefix}${name}`]),
    ...(field.properties ? findInvalidFieldNames(field.properties, `${prefix}${name}.`) : []),
    ...(field.items?.properties ? findInvalidFieldNames(field.items.properties, `${prefix}${name}[].`) : [])
]);
/** A one-line summary per field, for showing a schema change to the user. */
export const describeFields = (fields) => Object.entries(fields)
    .map(([name, field]) => {
    const type = field.type === 'array' && field.items ? `${field.items.type}[]` : field.type;
    const details = [
        type,
        ...(field.required ? ['required'] : []),
        ...(field.enum ? [field.enum.join(' | ')] : [])
    ];
    return `${name} (${details.join(', ')})${field.description ? `: ${field.description}` : ''}`;
})
    .join('\n');
//# sourceMappingURL=fields.js.map